import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { BookOpen, ArrowLeft, Download, Loader2, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth, getAuthHeaders } from "../App";
import DashboardHeader from "../components/DashboardHeader";
import ReactMarkdown from 'react-markdown';

const LectureNotesView = () => {
  const { notesId } = useParams();
  const navigate = useNavigate();
  const { user, token, refreshUser } = useAuth();
  const [notes, setNotes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchNotes();
  }, [notesId]);

  const fetchNotes = async () => {
    try {
      const response = await axios.get(`${API}/lecture-notes/${notesId}`, {
        headers: getAuthHeaders(token)
      });
      setNotes(response.data);
    } catch (error) {
      toast.error("Failed to load lecture notes");
      navigate("/lecture-notes");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if ((user?.credits || 0) < 2) {
      toast.error("Insufficient credits for download");
      navigate("/pricing");
      return;
    }

    setDownloading(true);
    try {
      const response = await axios.post(
        `${API}/lecture-notes/${notesId}/download`,
        {},
        { 
          headers: getAuthHeaders(token),
          responseType: 'blob'
        }
      );
      
      await refreshUser();
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `lecture_notes_${notesId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success("Lecture notes PDF downloaded!");
    } catch (error) {
      if (error.response?.status === 402) {
        toast.error("Insufficient credits");
        navigate("/pricing");
      } else {
        toast.error("Failed to download");
      }
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <DashboardHeader />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#4F46E5]" />
        </div>
      </div>
    );
  }

  if (!notes) return null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="lecture-notes-view">
      <DashboardHeader />

      <main className="container-main py-8 max-w-4xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate("/lecture-notes")} className="p-2">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {notes.title}
              </h1>
              <p className="text-slate-500 text-sm">
                Created {new Date(notes.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>
          <Button
            onClick={handleDownload}
            disabled={downloading}
            className="btn-primary gap-2"
          >
            {downloading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            Download PDF (2 credits)
          </Button>
        </div>

        {/* Summary */}
        {notes.summary && (
          <Card className="card-base mb-6 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
            <CardContent 
              className="p-6"
              style={{ 
                userSelect: 'none', 
                WebkitUserSelect: 'none',
                MozUserSelect: 'none',
                msUserSelect: 'none'
              }}
              onCopy={(e) => e.preventDefault()}
              onCut={(e) => e.preventDefault()}
            >
              <h3 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-[#4F46E5]" />
                Summary
              </h3>
              <p className="text-slate-700">{notes.summary}</p>
            </CardContent>
          </Card>
        )}

        {/* Key Points */}
        {notes.key_points?.length > 0 && (
          <Card className="card-base mb-6">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                Key Points
              </CardTitle>
            </CardHeader>
            <CardContent
              style={{ 
                userSelect: 'none', 
                WebkitUserSelect: 'none',
                MozUserSelect: 'none',
                msUserSelect: 'none'
              }}
              onCopy={(e) => e.preventDefault()}
              onCut={(e) => e.preventDefault()}
            >
              <ul className="space-y-3">
                {notes.key_points.map((point, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">
                      {idx + 1}
                    </span>
                    <span className="text-slate-700">{point}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Main Content */}
        {notes.content && (
          <Card className="card-base">
            <CardHeader>
              <CardTitle className="text-lg">Detailed Notes</CardTitle>
            </CardHeader>
            <CardContent
              style={{ 
                userSelect: 'none', 
                WebkitUserSelect: 'none',
                MozUserSelect: 'none',
                msUserSelect: 'none'
              }}
              onCopy={(e) => e.preventDefault()}
              onCut={(e) => e.preventDefault()}
            >
              <div className="prose prose-slate max-w-none">
                <ReactMarkdown
                  components={{
                    h1: ({children}) => <h1 className="text-2xl font-bold text-slate-900 mt-6 mb-4">{children}</h1>,
                    h2: ({children}) => <h2 className="text-xl font-semibold text-slate-800 mt-5 mb-3">{children}</h2>,
                    h3: ({children}) => <h3 className="text-lg font-medium text-slate-700 mt-4 mb-2">{children}</h3>,
                    p: ({children}) => <p className="text-slate-600 mb-3 leading-relaxed">{children}</p>,
                    ul: ({children}) => <ul className="list-disc list-inside space-y-1 mb-4 text-slate-600">{children}</ul>,
                    ol: ({children}) => <ol className="list-decimal list-inside space-y-1 mb-4 text-slate-600">{children}</ol>,
                    li: ({children}) => <li className="mb-1">{children}</li>,
                    strong: ({children}) => <strong className="font-semibold text-slate-800">{children}</strong>,
                  }}
                >
                  {notes.content}
                </ReactMarkdown>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default LectureNotesView;
